import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { type ApiArchive, EnumArchiveRunState } from '@Model';
import { ArchivesRepository } from '@Repositories/Documents';
import { isOk } from '@synqo/frontend-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useArchiveDownload } from './useArchiveDownload';

const ARCHIVES = 'archives';

/** Whether the run writing the archive has not finished yet. */
// @ai-generated 2026-09-30T03:06:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function isBeingWritten(archive: ApiArchive | undefined): boolean {
    return archive?.state === EnumArchiveRunState.Scheduled || archive?.state === EnumArchiveRunState.Running;
}

/**
 * Loads an archive and reloads it whenever the service reports a change; downloads the archive, or asks for
 * an expired or failed one to be created again.
 */
// @ai-generated 2026-09-30T07:43:44+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function useArchive(archiveId: string) {
    const archives = useDocumentsRepository(ArchivesRepository);
    const downloadArchive = useArchiveDownload();
    const queryClient = useQueryClient();
    const queryKey = ['documents', 'archives', archiveId];

    const info = useQuery({
        queryKey,
        queryFn: () => archives.describe(archiveId),
    });

    const { refetch } = info;
    const reload = useCallback(() => void refetch(), [refetch]);
    useRemoteCacheInvalidation(ARCHIVES, reload);

    const { mutateAsync: download } = useMutation({
        mutationFn: () => downloadArchive(archiveId),
    });

    const { mutateAsync: rebuild } = useMutation({
        mutationFn: () => archives.rebuild(archiveId),
        onSuccess: (result) => {
            if (isOk(result)) {
                queryClient.setQueryData(queryKey, result);
            }
        },
    });

    return { info, download, rebuild };
}
